"use client";

import Image from "next/image";
// CMS-supplied hrefs are emitted verbatim - do NOT swap this for @repo/ui/navigation.
// The CMS localizes its own URLs (e.g. "cl/es/centro-de-ayuda/") and also returns
// external/relative ones, so prefixing here would double or corrupt the locale.
import Link from "next/link";

import { ChevronRight } from "lucide-react";

import { Card, CardContent, CardFooter } from "@repo/ui/components/ui/card";
import type { IconFeatureCard as IconFeatureCardType } from "@repo/ui/types/cms";

interface IconFeatureCardProps {
  item: IconFeatureCardType;
  testId?: string;
}

export default function IconFeatureCard({ item, testId }: Readonly<IconFeatureCardProps>) {
  const isBordered = item?.add_border && item.add_border.length > 0;

  if (isBordered)
    return (
      <Card className="gap-0 rounded-lg border border-brand-black-70 bg-white py-0 shadow-none !p-0">
        <CardContent className="flex items-center gap-3 p-3" data-testid={testId}>
          {item?.icon && (
            <div className="bg-brand-blue-40 shrink-0 rounded-full p-2.5">
              <div className="relative h-5 w-5">
                <Image src={item.icon} alt={item.title || "icon"} fill className="object-contain" />
              </div>
            </div>
          )}
          <div>
            {item.title && <p className="text-brand-blue text-sm font-bold">{item.title}</p>}
            {item.description && <p className="text-brand-black-30 text-xs">{item.description}</p>}
          </div>
        </CardContent>
        <CardFooter className="mx-3 flex items-center justify-center border-t border-brand-black-70 !p-2">
          {item?.link_cta && item?.link_text && (
            <Link href={item.link_cta} target="_blank">
              <button
                className="text-brand-cyan cursor-pointer text-sm font-medium"
                data-testid={`${testId}-${item?.link_text}-btn`}
              >
                {item.link_text}
                <ChevronRight className="ml-1 inline-block" size={14} />
              </button>
            </Link>
          )}
        </CardFooter>
      </Card>
    );

  return (
    <div className="md:px-8" data-testid={testId}>
      {item.link_cta ? (
        <Link
          href={item.link_cta}
          target="_blank"
          className="block"
          data-testid={`${testId}-${item?.link_text}-link`}
        >
          <div className="text-brand-blue flex flex-row items-center gap-4">
            {item?.icon && (
              <div className="bg-brand-blue-40 rounded-full p-3">
                <div className="relative h-5 w-5">
                  <Image
                    src={item.icon}
                    alt={item.title || "icon"}
                    fill
                    className="object-contain"
                  />
                </div>
              </div>
            )}
            {item?.title && <p className="font-medium">{item.title}</p>}
          </div>
        </Link>
      ) : (
        <div className="text-brand-blue flex flex-row items-center gap-4">
          {item?.icon && (
            <div className="bg-brand-blue-40 rounded-full p-3">
              <div className="relative h-5 w-5">
                <Image src={item.icon} alt={item.title || "icon"} fill className="object-contain" />
              </div>
            </div>
          )}
          {item?.title && <p className="font-medium">{item.title}</p>}
        </div>
      )}

      {item?.description && <p className="text-sm text-black">{item.description}</p>}

      {item?.link_cta && item?.link_text && (
        <Link href={item.link_cta} target="_blank">
          <button
            className="text-brand-cyan cursor-pointer font-medium underline"
            data-testid={`${testId}-${item?.link_text}-btn`}
          >
            {item.link_text}
          </button>
        </Link>
      )}
    </div>
  );
}
